import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { nanoid } from 'nanoid'

import Layout from '@components/layout/Layout'
import Title from '@components/title/Title'
import LabelAndTextInput from '@components/inputs/LabelAndTextInput'
import SelectBoxAndLabel from '@components/select/SelectBoxAndLabel'
import Button from '@components/buttons/Button'
import BrandedLoader from '@components/loaders/BrandedLoader'
import Pictogram from '@components/pictogram/Pictogram'
import Label from '@components/labels/Label'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBuilding } from '@fortawesome/free-solid-svg-icons'
import {
	defaultInputValidator,
	defaultNoNumberInputValidator,
	emailValidator,
	numberValidator,
	requiredValidator,
} from '@/utils/validators'
import { useValidatorContext } from '@/utils'
import type { validatorFunc } from '@/utils/validatorProvider'
import { useForm } from '@/providers/formProvider'
import { useSessionTokenVerification } from '@/hooks/useSessionToken'
import Validator from '@/pages/form/validator/Validator'
import InvitationResultModal from './InvitationResultModal'
import type { InvitationResult } from './InvitationResultModal'
import ButtonIconText from '@components/buttons/ButtonIconText'
import deleteicon from '@assets/form/DeleteIcon.svg'
import house from '@assets/form/house.svg'
import room1 from '@assets/form/number-1.svg'
import room2 from '@assets/form/number-2.svg'
import room3 from '@assets/form/number-3.svg'
import room4 from '@assets/form/number-4.svg'
import room5 from '@assets/form/number-5.svg'
import personIcon from '@assets/form/person.svg'
import corporationIcon from '@assets/form/moral.svg'

import '@/pages/form/sections/Section.css'
import './FormAdmin.css'
import './FormCreation.css'

const ROOM_OPTIONS = [
	{ value: '1', icon: room1, label: '1 pièce' },
	{ value: '2', icon: room2, label: '2 pièces' },
	{ value: '3', icon: room3, label: '3 pièces' },
	{ value: '4', icon: room4, label: '4 pièces' },
	{ value: '5', icon: room5, label: '5 pièces' },
]

const VAT_OPTIONS: { [key: string]: string } = {
	'': 'Sélectionner...',
	'5,5%': '5,5%',
	'7%': '7%',
	'10%': '10%',
	'20%': '20%',
}

const USAGE_OPTIONS: { [key: string]: string } = {
	'': 'Sélectionner...',
	'Résidence principale': 'Résidence principale',
	'Résidence secondaire': 'Résidence secondaire',
	'Investissement locatif': 'Investissement locatif',
}

const PARKING_OPTIONS: { [key: string]: string } = {
	'': 'Sélectionner...',
	'sans parking': 'Sans parking',
	'parking extérieur (aérien)': 'Parking extérieur (aérien)',
	'parking couvert (rdc)': 'Parking couvert (rdc)',
	'parking sous-sol': 'Parking sous-sol',
	'parking boxé': 'Parking boxé',
}

const RESIDENCE_TYPE_OPTIONS: { [key: string]: string } = {
	'': 'Sélectionner...',
	'Ancien': 'Ancien',
	'Neuf (accession libre)': 'Neuf (accession libre)',
	'Neuf (tourisme)': 'Neuf (tourisme)',
	'Neuf (étudiant)': 'Neuf (étudiant)',
	'Neuf (seniors)': 'Neuf (seniors)',
}

const GENDER_OPTIONS: { [key: string]: string } = {
	'': 'Sélectionner',
	mr: 'M',
	mrs: 'Mme',
}

interface Buyer {
	id: string
	gender: string
	firstName: string
	lastName: string
	email: string
	phone: string
	corporationName: string
}

function newBuyer(): Buyer {
	return { id: nanoid(), gender: '', firstName: '', lastName: '', email: '', phone: '', corporationName: '' }
}

function getCombinedResidenceType(residenceType: string, managedResidenceType: string): string {
	if (residenceType === 'Neuf (résidences gérées)' && managedResidenceType) {
		return `Neuf (${managedResidenceType})`
	}
	return residenceType
}

function parseCombinedResidenceType(value: string): { residenceType: string; managedResidenceType: string } {
	switch (value) {
		case 'Neuf (tourisme)': return { residenceType: 'Neuf (résidences gérées)', managedResidenceType: 'tourisme' }
		case 'Neuf (étudiant)': return { residenceType: 'Neuf (résidences gérées)', managedResidenceType: 'étudiant' }
		case 'Neuf (seniors)': return { residenceType: 'Neuf (résidences gérées)', managedResidenceType: 'seniors' }
		default: return { residenceType: value, managedResidenceType: '' }
	}
}

interface RoomSelectorProps {
	value: string
	onChange: (value: string) => void
	validator?: validatorFunc
}

interface AccommodationSelectorProps {
	value: string
	onChange: (value: string) => void
	validator?: validatorFunc
}

interface BuyerTypeSelectorProps {
	value: string
	onChange: (value: string) => void
	validator?: validatorFunc
}

interface BuyerFormProps {
	buyer: Buyer
	buyerType: string
	index: number
	canRemove: boolean
	onRemove: () => void
	onChange: (field: keyof Buyer, value: string) => void
}

function AccommodationSelector(props: AccommodationSelectorProps) {
	const [errors, setErrors] = useState<string[]>([])
	const ref = useRef<HTMLInputElement>(null)

	return (
		<Validator
			id='accommodationType'
			validator={props.validator}
			reference={ref}
			onError={setErrors}
		>
			<div className='accommodationSelector'>
				<Label htmlFor='accommodationType'>Type de bien</Label>
				<input id='accommodationType' ref={ref} type='hidden' value={props.value} readOnly />
				<div className='pictograms accommodationSelectorPictograms'>
					<Pictogram
						id='accommodation-house'
						src={house}
						className={
							'picto1 accommodationPicto' + (props.value === 'Maison' ? ' active' : '')
						}
						onChange={() => props.onChange('Maison')}
						style={{ cursor: 'pointer' }}
					>
						Maison
					</Pictogram>
					<div
						className={
							'picto1 accommodationPicto accommodationPictoIcon' +
							(props.value === 'Appartement' ? ' active' : '')
						}
						onClick={() => props.onChange('Appartement')}
						role='button'
						tabIndex={0}
						onKeyDown={event => {
							if (event.key !== 'Enter' && event.key !== ' ') return
							event.preventDefault()
							props.onChange('Appartement')
						}}
						style={{ cursor: 'pointer' }}
					>
						<FontAwesomeIcon icon={faBuilding} />
						<p>Appartement</p>
					</div>
				</div>
				{errors.map((error, index) =>
					error !== '' ? (
						<span key={index} className='error'>
							{error}
						</span>
					) : null,
				)}
			</div>
		</Validator>
	)
}

function RoomSelector(props: RoomSelectorProps) {
	const [errors, setErrors] = useState<string[]>([])
	const ref = useRef<HTMLInputElement>(null)

	return (
		<Validator id='rooms' validator={props.validator} reference={ref} onError={setErrors}>
			<div className='roomSelector'>
				<Label htmlFor='rooms'>Nombre de pièces</Label>
				<input id='rooms' ref={ref} type='hidden' value={props.value} readOnly />
				<div className='pictograms roomSelectorPictograms'>
					{ROOM_OPTIONS.map(option => (
						<Pictogram
							key={option.value}
							id={`room-${option.value}`}
							src={option.icon}
							className={'picto1 roomPicto' + (props.value === option.value ? ' active' : '')}
							onChange={() => props.onChange(option.value)}
							style={{ cursor: 'pointer' }}
						>
							{option.label}
						</Pictogram>
					))}
				</div>
				{errors.map((error, index) =>
					error !== '' ? (
						<span key={index} className='error'>
							{error}
						</span>
					) : null,
				)}
			</div>
		</Validator>
	)
}

function BuyerTypeSelector(props: BuyerTypeSelectorProps) {
	const [errors, setErrors] = useState<string[]>([])
	const ref = useRef<HTMLInputElement>(null)

	return (
		<Validator id='buyerType' validator={props.validator} reference={ref} onError={setErrors}>
			<div className='buyerTypeSelector'>
				<Label htmlFor='buyerType'>Type d'acquéreur</Label>
				<input id='buyerType' ref={ref} type='hidden' value={props.value} readOnly />
				<div className='pictograms buyerTypeSelectorPictograms'>
					<Pictogram
						id='buyerType-person'
						src={personIcon}
						className={'picto1 buyerTypePicto' + (props.value === 'person' ? ' active' : '')}
						onChange={() => props.onChange('person')}
						style={{ cursor: 'pointer' }}
					>
						Personne physique
					</Pictogram>
					<Pictogram
						id='buyerType-corporation'
						src={corporationIcon}
						className={'picto1 buyerTypePicto' + (props.value === 'corporation' ? ' active' : '')}
						onChange={() => props.onChange('corporation')}
						style={{ cursor: 'pointer' }}
					>
						Personne morale
					</Pictogram>
				</div>
				{errors.map((error, index) =>
					error !== '' ? (
						<span key={index} className='error'>
							{error}
						</span>
					) : null,
				)}
			</div>
		</Validator>
	)
}

function BuyerForm({ buyer, buyerType, index, canRemove, onRemove, onChange }: BuyerFormProps) {
	const title = buyerType === 'corporation'
		? `Personne morale ${index + 1}`
		: `Acquéreur ${index + 1}`

	return (
		<div className='buyerEntry'>
			<div className='buyerEntryHeader'>
				<span className='buyerEntryTitle'>{title}</span>
				{canRemove && (
					<ButtonIconText
						id='suppButton'
						icon={deleteicon}
						text='Supprimer ce co-acquéreur'
						onClick={onRemove}
					/>
				)}
			</div>
			{buyerType === 'corporation' && (
				<LabelAndTextInput
					id={`${buyer.id}-corporationName`}
					label='Raison sociale'
					name={`${buyer.id}-corporationName`}
					placeholder='Ex : Dupont SAS'
					value={buyer.corporationName}
					validator={defaultInputValidator}
					onChange={v => onChange('corporationName', v)}
				/>
			)}
			<div className='fitContentLine'>
				<SelectBoxAndLabel
					id={`${buyer.id}-gender`}
					text={buyerType === 'corporation' ? 'Civilité représentant' : 'Civilité'}
					options={GENDER_OPTIONS}
					value={buyer.gender}
					validator={v => [requiredValidator(v)]}
					onChange={v => onChange('gender', v)}
				/>
			</div>
			<div className='fieldLine'>
				<LabelAndTextInput
					id={`${buyer.id}-firstName`}
					label={buyerType === 'corporation' ? 'Prénom représentant' : 'Prénom'}
					name={`${buyer.id}-firstName`}
					placeholder='Ex : Jean'
					value={buyer.firstName}
					validator={defaultNoNumberInputValidator}
					onChange={v => onChange('firstName', v)}
				/>
				<LabelAndTextInput
					id={`${buyer.id}-lastName`}
					label={buyerType === 'corporation' ? 'Nom représentant' : 'Nom'}
					name={`${buyer.id}-lastName`}
					placeholder='Ex : Dupont'
					value={buyer.lastName}
					validator={defaultNoNumberInputValidator}
					onChange={v => onChange('lastName', v)}
				/>
			</div>
			<div className='fieldLine'>
				<LabelAndTextInput
					id={`${buyer.id}-email`}
					label='Email'
					name={`${buyer.id}-email`}
					placeholder='Ex : jean.dupont@exemple.fr'
					value={buyer.email}
					validator={v => [requiredValidator(v), emailValidator(v)]}
					onChange={v => onChange('email', v)}
				/>
				<LabelAndTextInput
					id={`${buyer.id}-phone`}
					label='Téléphone'
					name={`${buyer.id}-phone`}
					placeholder='Ex : +33612345678'
					value={buyer.phone}
					validator={defaultInputValidator}
					onChange={v => onChange('phone', v)}
				/>
			</div>
		</div>
	)
}

function FormCreation() {
	const validator = useValidatorContext()
	const form = useForm()
	const navigate = useNavigate()
	const { token, isVerifying } = useSessionTokenVerification({
		mode: 'require',
		redirectTo: '/form-admin-connection',
	})
	const [programName, setProgramName] = useState<string>('Nom du programme')
	const [city, setCity] = useState<string>('')
	const [developer, setDeveloper] = useState<string>('')
	const [broker, setBroker] = useState<string>('')
	const [buyerType, setBuyerType] = useState<string>('')
	const [buyers, setBuyers] = useState<Buyer[]>([newBuyer()])
	const [accommodationType, setAccommodationType] = useState<string>('')
	const [rooms, setRooms] = useState<string>('')
	const [residenceType, setResidenceType] = useState<string>('')
	const [lotNumber, setLotNumber] = useState<string>('')
	const [area, setArea] = useState<string>('')
	const [parking, setParking] = useState<string>('')
	const [usage, setUsage] = useState<string>('')
	const [vat, setVat] = useState<string>('')
	const [managedResidenceType, setManagedResidenceType] = useState<string>('')
	const [price, setPrice] = useState<string>('')
	const [submitError, setSubmitError] = useState<string>('')
	const [created, setCreated] = useState<InvitationResult | null>(null)
	// Creating the form also sends the invitation emails, so the round trip can
	// take a few seconds: the button reports it rather than looking inert.
	const [submitting, setSubmitting] = useState<boolean>(false)
	const roomsValidator = (value: string) => [requiredValidator(value), numberValidator(value)]

	const addBuyer = () => setBuyers(prev => [...prev, newBuyer()])
	const removeBuyer = (id: string) => setBuyers(prev => prev.filter(b => b.id !== id))
	const updateBuyer = (id: string, field: keyof Buyer, value: string) =>
		setBuyers(prev => prev.map(b => b.id === id ? { ...b, [field]: value } : b))

	const fillWithMockData = () => {
		setProgramName('Les Terrasses du Soleil')
		setCity('Cannes')
		setDeveloper('Nexity')
		setBroker('Ficanas')
		setBuyerType('person')
		setBuyers([{
			id: nanoid(),
			gender: 'mr',
			firstName: 'Jean',
			lastName: 'Dupont',
			email: 'jean.dupont@example.com',
			phone: '+33612345678',
			corporationName: '',
		}])
		setAccommodationType('Appartement')
		setRooms('3')
		setResidenceType('Neuf (résidences gérées)')
		setManagedResidenceType('tourisme')
		setLotNumber('A-042')
		setArea('65 m²')
		setParking('parking sous-sol')
		setUsage('Investissement locatif')
		setVat('20%')
		setPrice('450 000 €')
	}

	const handleValidate = async () => {
		if (!validator.validate()) {
			setSubmitError('Veuillez vérifier votre saisie, certains champs sont incorrects ou manquants.')
			return
		}
		setSubmitError('')
		setSubmitting(true)

		try {
			const persons = buyerType === 'person'
				? buyers.map(b => ({ id: b.id, gender: b.gender, firstName: b.firstName, lastName: b.lastName, email: b.email, phone: b.phone }))
				: []
			const corporations = buyerType === 'corporation'
				? buyers.map(b => ({ id: b.id, name: b.corporationName, delegateGender: b.gender, delegateFirstName: b.firstName, delegateLastName: b.lastName, delegateEmail: b.email, delegatePhone: b.phone }))
				: []
			const buyersJson = JSON.stringify({ buyerType, persons, corporations })

			const response = await form.client.createForm({
				token,
				name: programName,
				city,
				developer,
				broker,
				accommodationType,
				rooms,
				residenceType,
				managedResidenceType,
				lotNumber,
				area,
				parking,
				usage,
				vat,
				price,
				buyersJson,
				buyerFirstName: '',
				buyerLastName: '',
				buyerEmail: '',
				buyerPhone: '',
			})
			setCreated({
				link: `${window.location.origin}/form?token=${response.shareToken}`,
				emailedTo: response.emailedTo,
				emailFailures: response.emailFailures,
			})
		} catch (error) {
			console.error('Error creating form: ', error)
			setSubmitError('Une erreur est survenue lors de la création du formulaire.')
		} finally {
			setSubmitting(false)
		}
	}

	if (isVerifying || !token) return <BrandedLoader />

	const addBuyerLabel = buyerType === 'corporation' ? 'Ajouter un co-représentant' : 'Ajouter un co-acquéreur'

	return (
		<Layout showSearchBar={false}>
			<div className='section whiteBackground'>
				<div id='programName'>
					<Title editable={true} value={programName} onChange={setProgramName}>
						Nom du programme
					</Title>
				</div>
				<LabelAndTextInput
					id='city'
					label='Ville'
					name='city'
					placeholder='Ex : Cannes'
					value={city}
					validator={defaultInputValidator}
					onChange={setCity}
				/>
				<LabelAndTextInput
					id='developer'
					label='Promoteur'
					name='developer'
					autocomplete='developer'
					placeholder='Ex : Promoteur'
					value={developer}
					validator={defaultInputValidator}
					onChange={setDeveloper}
				/>
				<LabelAndTextInput
					id='broker'
					label='Commercialisateur'
					name='broker'
					autocomplete='broker'
					placeholder='Ex : Commercialisateur'
					value={broker}
					validator={defaultInputValidator}
					onChange={setBroker}
				/>
			</div>
			<div className='section whiteBackground'>
				<div className='sectionHeader'>
					<Title>Acquéreur(s)</Title>
				</div>
				<div className='formContent'>
					<BuyerTypeSelector
						value={buyerType}
						validator={defaultInputValidator}
						onChange={value => {
							setBuyerType(value)
							setBuyers([newBuyer()])
						}}
					/>
					{buyerType !== '' && (
						<>
							{buyers.map((buyer, index) => (
								<BuyerForm
									key={buyer.id}
									buyer={buyer}
									buyerType={buyerType}
									index={index}
									canRemove={buyers.length > 1}
									onRemove={() => removeBuyer(buyer.id)}
									onChange={(field, value) => updateBuyer(buyer.id, field, value)}
								/>
							))}
							<div className='buyerAddButton'>
								<Button onClick={event => { event.preventDefault(); addBuyer() }}>
									+ {addBuyerLabel}
								</Button>
							</div>
						</>
					)}
				</div>
			</div>
			<div className='section whiteBackground'>
				<div className='sectionHeader'>
					<Title>Récapitulatif de votre bien</Title>
				</div>
				<div className='formContent'>
					<AccommodationSelector
						value={accommodationType}
						validator={defaultInputValidator}
						onChange={setAccommodationType}
					/>
					<RoomSelector value={rooms} validator={roomsValidator} onChange={setRooms} />
					<div className='fieldLine'>
						<SelectBoxAndLabel
							id='residenceType'
							text='Type de résidence'
							options={RESIDENCE_TYPE_OPTIONS}
							value={getCombinedResidenceType(residenceType, managedResidenceType)}
							validator={defaultInputValidator}
							onChange={value => {
								const parsed = parseCombinedResidenceType(value)
								setResidenceType(parsed.residenceType)
								setManagedResidenceType(parsed.managedResidenceType)
							}}
						/>
						<LabelAndTextInput
							id='lotNumber'
							label='Numéro de lot'
							name='lotNumber'
							placeholder='Ex : no 008'
							value={lotNumber}
							validator={defaultInputValidator}
							onChange={setLotNumber}
						/>
						<LabelAndTextInput
							id='area'
							label='Superficie'
							name='area'
							placeholder='Ex : 100 m²'
							value={area}
							validator={defaultInputValidator}
							onChange={setArea}
						/>
					</div>
					<div className='fieldLine'>
						<SelectBoxAndLabel
							id='parking'
							text='Parking'
							options={PARKING_OPTIONS}
							value={parking}
							validator={defaultInputValidator}
							onChange={setParking}
						/>
						<SelectBoxAndLabel
							id='usage'
							text='Usage du bien'
							options={USAGE_OPTIONS}
							value={usage}
							validator={defaultInputValidator}
							onChange={setUsage}
						/>
					</div>
					<div className='fieldLine'>
						<SelectBoxAndLabel
							id='vat'
							text='Taux de TVA'
							options={VAT_OPTIONS}
							value={vat}
							validator={defaultInputValidator}
							onChange={setVat}
						/>
						<LabelAndTextInput
							id='price'
							label='Prix'
							name='price'
							placeholder='Ex : 450 000 €'
							value={price}
							validator={defaultInputValidator}
							onChange={setPrice}
						/>
					</div>
				</div>
			</div>
			{submitError !== '' && <p className='error' style={{ textAlign: 'center' }}>{submitError}</p>}
			<div className='formCreationActions'>
				{process.env.NODE_ENV === 'development' && (
					<Button onClick={fillWithMockData}>Remplir (dev)</Button>
				)}
				<Button onClick={handleValidate} loading={submitting}>
					{submitting ? 'Envoi en cours...' : 'Valider'}
				</Button>
			</div>
			{created !== null && (
				<InvitationResultModal
					title='Le formulaire a bien été créé'
					result={created}
					closeLabel='Retour à mes formulaires'
					onClose={() => navigate('/form-admin')}
				/>
			)}
		</Layout>
	)
}

export default FormCreation
